/**
 * API service for making HTTP requests to the backend
 */

import config, { getApiUrl } from '../config/config';

class ApiService {
  constructor() {
    this.baseURL = config.baseURL;
    this.timeout = 30000; // 30 seconds
  }

  /**
   * Make an HTTP request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options (method, body, headers, etc.)
   * @returns {Promise} Response data
   */
  async request(endpoint, options = {}) {
    const url = getApiUrl(endpoint);
    
    // Merge default headers with custom headers
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };

    const requestConfig = {
      ...options,
      headers,
    };

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...requestConfig,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        const error = new Error(data.message || data || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout. Please try again.');
      }
      
      console.error('API Error:', error);
      throw error;
    }
  }

  /**
   * Get all products
   * @param {Object} params - Query parameters (page, limit, etc.)
   * @returns {Promise} Products data
   */
  async getAllProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/getAllProducts${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint, {
      headers: {
        'Cookie': 'sid=e48d5cdf-5b64-4041-a498-f519950be914',
      },
    });
  }
}

// Export singleton instance
export default new ApiService();

